import { useEffect } from 'react';

import { DateTime } from 'luxon';
import { FormikValues, useFormik } from 'formik';

import {
  AllowancesNames,
  ContractTypes,
  ProfessionalReplacementTypes,
  calendarDate,
  createWorkshift,
  createWorkshiftContract,
  getPharmacyCalendar,
  getRecurrence,
  pharmacyActions,
  reset,
  resetLongTermContractAttributes,
  resetStatus,
  successOrSelector,
} from '@pharmaplan/common';
import Validator from '../../components/SelfService/Workshifts/Validator';
import {
  resetDrawer,
  setButtons,
  setPagination,
} from '../../actions/drawerActions';

import useGetChangedContract from '../LongTermContract/useGetChangedContract';
import { showSuccess } from '../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import { makeReqParams, setHour } from '../../helpers/workshift/functions';
import { DurationTypes } from '../../helpers/Constants';
import useSaveAndRemain from '../useSaveAndCreateAnother';
import strings from '../../localization';

import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import useRecurrence from '../useRecurrence';

const { pharmacist } = ProfessionalReplacementTypes;
const { weekDayPaidHourlyRate, weekendPaidHourlyRate } = AllowancesNames;
const { notRepeat } = DurationTypes;

const todaysISO = DateTime.now().toISO({ includeOffset: false }) ?? '';

const useAddWorkshift = (date: string | null) => {
  const dispatch = useAppDispatch();
  const { saveAndRemain, handleSetSave, resetSaveAndRemain } = useSaveAndRemain();
  const { getChangedItems, comparator } = useGetChangedContract();

  const recurrence = useAppSelector(getRecurrence);
  const cDate = useAppSelector(calendarDate);
  const createSuccess = useAppSelector((state) =>
    successOrSelector(
      [
        pharmacyActions.createWorkshift,
        pharmacyActions.createWorkshiftContract,
      ],
      state,
    ));

  const luxonDate = DateTime.fromISO(date || todaysISO);
  const parsedDate = date ? DateTime.fromISO(date) : null;

  const onSubmit = (values: FormikValues) => {
    if (values?.contractType === ContractTypes.longTerm) {
      const { startDate, endDate } = comparator();

      const dateParams = {
        startDate,
        endDate,
      };

      console.log({
        allowanceNotes: values?.allowanceNotes,
        isLongTermContract: values?.contractType === ContractTypes.longTerm,
        shiftContractAttributes: getChangedItems(),
        typeReplacement: values?.replacementType,
        ...makeReqParams(values, recurrence, dateParams),
      });

      dispatch(
        createWorkshiftContract({
          allowanceNotes: values?.allowanceNotes,
          isLongTermContract: values?.contractType === ContractTypes.longTerm,
          shiftContractAttributes: getChangedItems(),
          typeReplacement: values?.replacementType,
          ...makeReqParams(values, recurrence, dateParams),
        }),
      );

      return;
    }

    // NOTE: THE ORDER MATTERS
    dispatch(
      createWorkshift({
        typeReplacement: values?.replacementType,
        ...makeReqParams(values, recurrence),
      }),
    );
  };

  const formik = useFormik({
    initialValues: {
      datePicker: parsedDate,
      selectedDate: luxonDate,
      allDay: false,
      startTime: null,
      endTime: null,
      startDate: null,
      endDate: null,
      notes: '',
      recurrence: notRepeat,
      replacementType: pharmacist,
      contractType: ContractTypes.shortTerm,
      allowanceNotes: '',
      [weekDayPaidHourlyRate]: '',
      [weekendPaidHourlyRate]: '',
    },
    onSubmit,
    validationSchema: Validator(),
  });

  const { setFieldValue, handleSubmit } = formik ?? {};
  useRecurrence(formik);

  const handleReset = () => {
    dispatch(reset());
    if (saveAndRemain) {
      formik.resetForm();
      dispatch(resetLongTermContractAttributes());
      formik.setFieldValue('selectedDate', luxonDate);
      showSuccess(dispatch, strings.workshiftCreatedSuccessfully);
      resetSaveAndRemain();
      return;
    }
    dispatch(resetDrawer());
  };

  const setUserWorkTimings = () => {
    setFieldValue('startTime', setHour(luxonDate, 8));
    setFieldValue('endTime', setHour(luxonDate, 22));
  };

  useEffect(() => {
    dispatch(setPagination({ show: false }));
    dispatch(
      setButtons({
        show: true,
        primaryButton: {
          label: strings.createWorkshift,
          onClick: handleSubmit,
        },
        secondaryButton: {
          label: strings.saveAndCreate,
          onClick: handleSetSave(handleSubmit),
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (createSuccess) {
      dispatch(getPharmacyCalendar(cDate));
      handleReset();
      dispatch(resetStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSuccess]);

  useEffect(() => {
    if (formik.values.datePicker) {
      formik.setFieldValue('selectedDate', formik.values.datePicker);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.datePicker]);

  useEffect(
    () =>
      () => {
        dispatch(resetLongTermContractAttributes());
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { setUserWorkTimings, formik };
};

export default useAddWorkshift;
